"use client";

import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@shared/components/ui/chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { IconDownload, IconLoader2 } from "@tabler/icons-react";
import { gql } from "graphql-request";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { OrgProjectColorDot } from "@/components/org/org-project-chip";
import { PageHeader } from "@/components/page-header";
import { graphqlRequest } from "@/lib/graphql/client";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";
import {
  formatCostMicro,
  formatCostUsd,
  type UsageLedgerEvent,
  type UsageLedgerFilter,
  usageLedgerDocument,
  useUsageBuckets,
  useUsageLedger,
  useUsageSummary,
} from "@/lib/usage/usage.hooks";

const compactNumber = (value: number) =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));

/**
 * Parse an occurred_at value from the backend as UTC.
 * The backend returns "YYYY-MM-DD HH:MM:SS" without a timezone offset — it is
 * always UTC. Appending "Z" ensures the browser interprets it correctly instead
 * of treating it as local time.
 */
const parseUtcDate = (iso: string): Date => {
  const s = iso.trim();
  if (s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  return new Date(`${s.replace(" ", "T")}Z`);
};

const formatDateTime = (iso: string) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parseUtcDate(iso));

const chartConfig = {
  spend: { label: "Spend", color: "var(--chart-1)" },
} satisfies ChartConfig;

const PERIOD_30D = { preset: "last_30d" } as const;

const LEDGER_PAGE_SIZE = 50;

const REFETCH_INTERVAL_MS = 10_000;

/** Escape a value for a CSV cell: wrap in double-quotes, double internal quotes. */
const csvCell = (value: string): string => `"${value.replace(/"/g, '""')}"`;

const usageProjectsDocument = gql`
  query UsagePageProjects {
    me {
      org {
        projects {
          id
          name
          color
        }
      }
    }
  }
`;

type UsageProjectsQuery = {
  me: {
    org: {
      projects: Array<{ id: string; name: string; color: string | null }>;
    } | null;
  } | null;
};

type LedgerPageQuery = {
  usage_ledger: {
    events: UsageLedgerEvent[];
    next_cursor: string | null;
    has_next_page: boolean;
  };
};

/**
 * Exponential backoff retry helper.
 * Sleeps only between attempts, not after the final failure.
 */
const withRetry = async <T,>(fn: () => Promise<T>, attempts = 3): Promise<T> => {
  let lastErr: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt < attempts - 1) {
        const delay = 250 * 2 ** attempt;
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  throw lastErr;
};

export default function AnalyticsPage() {
  const [modelFilter, setModelFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [allEvents, setAllEvents] = useState<UsageLedgerEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadedExtraPages, setLoadedExtraPages] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportErrorGenRef = useRef(0);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreGenRef = useRef(0);

  const handleProjectFilterChange = (value: string) => {
    setProjectFilter(value);
    setModelFilter("all");
  };

  const projectsQuery = useGraphqlQuery<UsageProjectsQuery>({
    queryKey: ["usage", "projects"],
    document: usageProjectsDocument,
  });
  const projects = useMemo(() => projectsQuery.data?.me?.org?.projects ?? [], [projectsQuery.data]);
  const projectById = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);
  const selectedProjectId = projectFilter === "all" ? null : projectFilter;

  const activeFilter: UsageLedgerFilter | null = useMemo(() => {
    const filter: UsageLedgerFilter = {};
    if (modelFilter !== "all") {
      filter.model = modelFilter;
    }
    if (selectedProjectId) {
      filter.project_id = selectedProjectId;
    }
    return Object.keys(filter).length > 0 ? filter : null;
  }, [modelFilter, selectedProjectId]);

  const { data: summaryData, isLoading: summaryLoading } = useUsageSummary(PERIOD_30D, {
    projectId: selectedProjectId,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
  });
  const { data: dailyData, isLoading: dailyLoading } = useUsageBuckets(
    { count: 30 },
    {
      projectId: selectedProjectId,
      refetchInterval: REFETCH_INTERVAL_MS,
      refetchOnWindowFocus: "always",
    },
  );

  const {
    data: firstPageData,
    isLoading: firstPageLoading,
    isRefetching: firstPageRefetching,
  } = useUsageLedger(LEDGER_PAGE_SIZE, null, activeFilter, {
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
  });

  // Track the previous filter to reset pagination when it changes.
  const prevFilterRef = useRef(activeFilter);
  if (prevFilterRef.current !== activeFilter) {
    prevFilterRef.current = activeFilter;
    // Synchronously reset — React batches these with the next render.
    setAllEvents([]);
    setNextCursor(null);
    setHasMore(false);
    setLoadedExtraPages(false);
    setLoadMoreError(null);
    setLoadingMore(false);
    setExportError(null);
    exportErrorGenRef.current++;
    loadMoreGenRef.current++;
  }

  // Merge first page into allEvents on every successful fetch.
  // When the user has loaded extra pages, we only update the first-page slice
  // to preserve pagination state and avoid duplicating or resetting the cursor.
  useEffect(() => {
    if (!firstPageData?.usage_ledger) return;
    const fp = firstPageData.usage_ledger.events;
    const fpIds = new Set(fp.map((e) => e.id));

    setAllEvents((prev) => {
      if (fp.length === 0 && prev.length > 0) return prev;
      // Replace only the first-page portion; keep any load-more pages intact
      const extraPages = loadedExtraPages ? prev.filter((e) => !fpIds.has(e.id)) : [];
      return [...fp, ...extraPages];
    });

    // Only update cursor/hasMore when the user hasn't loaded extra pages,
    // so a background refetch can't rewind the pagination cursor.
    if (!loadedExtraPages) {
      setNextCursor(firstPageData.usage_ledger.next_cursor);
      setHasMore(firstPageData.usage_ledger.has_next_page);
    }
  }, [firstPageData, loadedExtraPages]);

  const summary = summaryData?.usage_summary;
  const dailyBuckets = useMemo(() => dailyData?.usage_buckets ?? [], [dailyData]);
  const modelRows = useMemo(() => summaryData?.usage_summary?.by_model ?? [], [summaryData]);

  // Chip list from usage_summary.by_model (order follows backend grouping)
  const modelNames = useMemo(() => modelRows.map((m) => m.model), [modelRows]);

  const dailySpendLine = useMemo(
    () =>
      dailyBuckets.map((b) => ({
        label: new Intl.DateTimeFormat("en-US", { month: "numeric", day: "numeric" }).format(
          new Date(`${b.date}T00:00:00`),
        ),
        spend: Number(b.cost_cents) / 100,
      })),
    [dailyBuckets],
  );

  const isLoading = summaryLoading || dailyLoading || firstPageLoading;

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadMoreError(null);
    setLoadingMore(true);
    const generation = loadMoreGenRef.current;
    try {
      const res = await withRetry(async () => {
        const vars: { first: number; after: string; filter?: UsageLedgerFilter } = {
          first: LEDGER_PAGE_SIZE,
          after: nextCursor,
        };
        if (activeFilter) {
          vars.filter = activeFilter;
        }
        return await graphqlRequest<LedgerPageQuery, { first: number; after: string; filter?: UsageLedgerFilter }>(
          usageLedgerDocument,
          vars,
        );
      });
      if (loadMoreGenRef.current !== generation) return;
      setAllEvents((prev) => [...prev, ...res.usage_ledger.events.filter((e) => !prev.some((p) => p.id === e.id))]);
      setNextCursor(res.usage_ledger.next_cursor);
      setHasMore(res.usage_ledger.has_next_page);
      setLoadedExtraPages(true);
    } catch (err) {
      if (loadMoreGenRef.current !== generation) return;
      console.error("Failed to load more events:", err);
      setLoadMoreError("Could not load more. Try again.");
    } finally {
      if (loadMoreGenRef.current === generation) {
        setLoadingMore(false);
      }
    }
  }, [nextCursor, loadingMore, activeFilter]);

  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    const generation = exportErrorGenRef.current;
    try {
      const rows: UsageLedgerEvent[] = [];
      let after: string | null = null;
      let hasNext = true;
      while (hasNext) {
        const res = await withRetry(async () => {
          const csvVars: { first: number; after?: string; filter?: UsageLedgerFilter } = { first: 250 };
          if (after) csvVars.after = after;
          if (activeFilter) csvVars.filter = activeFilter;
          return await graphqlRequest<LedgerPageQuery, { first: number; after?: string; filter?: UsageLedgerFilter }>(
            usageLedgerDocument,
            csvVars,
          );
        });
        rows.push(...res.usage_ledger.events);
        after = res.usage_ledger.next_cursor;
        hasNext = res.usage_ledger.has_next_page;
      }
      const header = [
        "Date",
        "Time",
        "Timestamp (UTC)",
        "Project",
        "Model",
        "Input tokens",
        "Output tokens",
        "Cost (USD)",
        "Cost (micro-USD)",
        "API key",
      ].join(",");
      const csv = [
        header,
        ...rows.map((e) => {
          const dt = parseUtcDate(e.occurred_at);
          return [
            csvCell(formatDate(dt.toISOString())),
            csvCell(dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })),
            csvCell(dt.toISOString()),
            csvCell(e.project_name ?? "—"),
            csvCell(e.model),
            e.input_tokens,
            e.output_tokens,
            csvCell(e.cost_micro !== "0" ? formatCostMicro(e.cost_micro) : formatCostUsd(e.cost_cents)),
            e.cost_micro,
            csvCell(e.api_key_label ?? "—"),
          ].join(",");
        }),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "usage-export.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export CSV:", err);
      if (exportErrorGenRef.current === generation) {
        setExportError("Could not export CSV. Try again.");
      }
    } finally {
      setExporting(false);
    }
  }, [activeFilter]);

  return (
    <div className="@container/main flex flex-1 flex-col gap-2">
      <div className="flex flex-col gap-4 md:gap-6">
        <PageHeader
          title="Usage"
          description="Monitor API spend, token consumption, and per-model usage across your organization."
          actions={
            <>
              {projects.length > 0 ? (
                <Select value={projectFilter} onValueChange={(value) => handleProjectFilterChange(String(value))}>
                  <SelectTrigger variant="filter" className="w-full sm:w-auto sm:min-w-40">
                    <SelectValue placeholder="View by Project">
                      <span className="inline-flex items-center gap-1">
                        {selectedProjectId
                          ? (() => {
                              const proj = projectById.get(selectedProjectId);
                              return proj ? (
                                <>
                                  <OrgProjectColorDot color={proj.color} />
                                  {proj.name}
                                </>
                              ) : (
                                "Unknown"
                              );
                            })()
                          : "All projects"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All projects</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="inline-flex items-center gap-1">
                          <OrgProjectColorDot color={p.color} />
                          {p.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="shrink-0"
                  onClick={handleExportCsv}
                  disabled={isLoading || exporting}
                >
                  {exporting ? (
                    <IconLoader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <IconDownload className="mr-2 size-4" />
                  )}
                  Export CSV
                </Button>
                {exportError && <p className="text-xs text-red-500">{exportError}</p>}
              </div>
            </>
          }
        />

        {/* Summary cards */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">
            Summary (last 30 days)
            {(firstPageRefetching || summaryLoading) && (
              <span className="ml-2 inline-flex items-center">
                <IconLoader2 className="size-3 animate-spin text-muted-foreground" />
              </span>
            )}
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Estimated spend</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{formatCostUsd(summary?.estimated_cost_cents ?? 0)}</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total input tokens</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{compactNumber(Number(summary?.input_tokens ?? 0))}</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total output tokens</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{compactNumber(Number(summary?.output_tokens ?? 0))}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Per-model breakdown */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">Per-model breakdown</h2>
          {summaryLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : modelRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No usage yet.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...modelRows]
                .sort((a, b) => Number(b.cost_cents) - Number(a.cost_cents))
                .map((row) => (
                  <Card key={row.model}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold">{row.model}</CardTitle>
                      <CardDescription>
                        {row.requests} {Number(row.requests) === 1 ? "request" : "requests"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-semibold">{formatCostUsd(row.cost_cents)}</p>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </div>

        {/* Daily spend chart */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Daily spend</CardTitle>
              <CardDescription>Last 30 days.</CardDescription>
            </CardHeader>
            <CardContent className="pl-0">
              {dailyLoading ? (
                <p className="py-10 text-center text-sm text-muted-foreground">Loading...</p>
              ) : (
                <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
                  <LineChart data={dailySpendLine} margin={{ left: 12, right: 12 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
                    />
                    <ChartTooltip
                      cursor={{ strokeDasharray: "4 4" }}
                      content={<ChartTooltipContent formatter={(v) => [`$${Number(v).toFixed(2)}`, "Spend"]} />}
                    />
                    <Line type="monotone" dataKey="spend" stroke="var(--color-spend)" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transaction log */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">
            Transaction log
            {firstPageRefetching && (
              <span className="ml-2 inline-flex items-center">
                <IconLoader2 className="size-3 animate-spin text-muted-foreground" />
              </span>
            )}
          </h2>
          <div className="mb-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={modelFilter === "all" ? "secondary" : "outline"}
              onClick={() => setModelFilter("all")}
            >
              All
            </Button>
            {modelNames.map((name) => (
              <Button
                key={name}
                size="sm"
                variant={modelFilter === name ? "secondary" : "outline"}
                className="max-w-[200px] truncate"
                onClick={() => setModelFilter(name)}
              >
                {name}
              </Button>
            ))}
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date &amp; time</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Input tokens</TableHead>
                  <TableHead className="text-right">Output tokens</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>API key</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {firstPageLoading && allEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      Loading usage...
                    </TableCell>
                  </TableRow>
                ) : allEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      No usage records yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  allEvents.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap">{formatDateTime(e.occurred_at)}</TableCell>
                      <TableCell className="max-w-[180px] truncate">{e.project_name ?? "—"}</TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-xs">{e.model}</TableCell>
                      <TableCell className="text-right tabular-nums">{compactNumber(e.input_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{compactNumber(e.output_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {e.cost_micro !== "0" ? formatCostMicro(e.cost_micro) : formatCostUsd(e.cost_cents)}
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate font-mono text-xs">
                        {e.api_key_label ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {hasMore && (
              <div className="flex items-center justify-center gap-3 border-t p-3">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? <IconLoader2 className="mr-2 size-4 animate-spin" /> : null}
                  Load more
                </Button>
                {loadMoreError && <p className="text-xs text-red-500">{loadMoreError}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
